import {useMutation} from '@apollo/client'
import React, {useEffect} from 'react'
import {Button, Modal, Spinner} from 'react-bootstrap'
import {FormattedMessage, defineMessages, useIntl} from 'react-intl'
import {DUPLICATE_PIPELINE} from '../../../gql/mutations/pipelineMutations'
import {IResDuplicatePipe} from '../../../types/pipelines'
import {toast} from 'react-toastify'
import {parseId} from '../../../helpers'

interface IModalDuplicatePipeline {
  isOpen: boolean
  onClose: () => void
  refetch: () => void
  infoEdit: {id: string; name: string}
}

const quote = '"'

const dupM = defineMessages({
  confirm: {id: 'leads.pipelines.duplicateConfirm', defaultMessage: 'Are you sure you want to duplicate this pipeline <b>{name}</b>?'},
  duplicate: {id: 'leads.pipelines.duplicate', defaultMessage: 'Duplicate'},
  toastDuplicated: {id: 'leads.pipelines.toast.duplicated', defaultMessage: 'Pipeline duplicated successfully'},
})

export const ModalDuplicatePipeline: React.FC<IModalDuplicatePipeline> = ({
  infoEdit,
  isOpen,
  onClose,
  refetch,
}) => {
  const intl = useIntl()
  const errMsg = (message?: string) =>
    intl.formatMessage({id: 'common.error.withMessage', defaultMessage: 'Error: {message}'}, {message})

  const [gqlAddEditPipeline, {data, loading, error}] =
    useMutation<IResDuplicatePipe>(DUPLICATE_PIPELINE)

  useEffect(() => {
    if (error) toast.error(errMsg(error.message))
  }, [error])

  useEffect(() => {
    if (!data) return
    if (data.duplicatePipeline?.pipeline?.id) {
      toast.success(intl.formatMessage(dupM.toastDuplicated))
      onClose()
      refetch()
    } else if (data.duplicatePipeline?.errors?.length > 0) {
      toast.error(errMsg(data.duplicatePipeline?.errors?.[0]?.message || intl.formatMessage({id: 'leads.add.toast.somethingHappened', defaultMessage: 'something happened'})))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  const handleDuplicate = () => {
    gqlAddEditPipeline({
      variables: {
        pipelineId: parseId(infoEdit.id),
      },
    })
  }

  return (
    <Modal show={isOpen} onHide={onClose}>
      <Modal.Header closeButton>
        <Modal.Title><FormattedMessage id='leads.pipelines.duplicateTooltip' defaultMessage='Duplicate Pipeline' /></Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <h2>
          {intl.formatMessage(dupM.confirm, {name: infoEdit.name, b: (chunks: (string | JSX.Element)[]) => <span className='text-info'>{quote}{chunks}{quote}</span>})}
        </h2>
      </Modal.Body>
      <Modal.Footer>
        <Button variant='secondary' onClick={onClose}>
          <FormattedMessage id='common.action.cancel' defaultMessage='Cancel' />
        </Button>
        <Button
          variant='primary'
          className='d-flex align-items-center'
          disabled={loading}
          onClick={handleDuplicate}
        >
          {loading && <Spinner animation='border' size='sm' />}
          {intl.formatMessage(dupM.duplicate)}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
